import { useEffect, useState, useRef } from "react";
import { api, formatApiError } from "@/lib/api";
import InventorySyncSettings from "@/components/InventorySyncSettings";
import { useAuth, isAdmin } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Buildings, Plus, X, Spinner, Lock, UploadSimple } from "@phosphor-icons/react";
import { mediaUrl } from "@/components/AssetImage";
import CustomTypesManager from "@/components/CustomTypesManager";
import IndustrySettings from "@/components/IndustrySettings";
import { toast } from "sonner";

export default function CompanySettings() {
  const { user, refreshCompany } = useAuth();
  const admin = isAdmin(user.role);
  const [company, setCompany] = useState(null);
  const [statuses, setStatuses] = useState([]);
  const [newStatus, setNewStatus] = useState("");
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const logoRef = useRef();

  const uploadLogo = async (file) => {
    if (!file) return;
    setUploadingLogo(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/company/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setCompany((c) => ({ ...c, logo_url: data.logo_url }));
      toast.success("Logo uploaded");
      refreshCompany();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setUploadingLogo(false); }
  };

  useEffect(() => {
    api.get("/company").then(({ data }) => { setCompany(data); setStatuses(data.statuses || []); });
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/company", { ...company, statuses });
      toast.success("Settings saved");
      refreshCompany();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  if (!admin) return (
    <div className="flex flex-col items-center justify-center py-24 text-center" data-testid="settings-noaccess">
      <Lock size={40} className="text-zinc-300 mb-3" />
      <p className="font-semibold">Admin access required</p>
      <p className="text-sm text-zinc-500">Only company admins can change these settings.</p>
    </div>
  );

  if (!company) return <div className="flex justify-center py-16"><Spinner size={28} className="animate-spin text-emerald-600" /></div>;

  const set = (k) => (e) => setCompany({ ...company, [k]: e.target.value });

  return (
    <div className="space-y-5 max-w-3xl" data-testid="settings-page">
      <div><h1 className="font-display text-2xl lg:text-3xl font-bold tracking-tight">Company Settings</h1><p className="text-zinc-500 text-sm mt-1">Manage your company profile and configuration.</p></div>

      <Tabs defaultValue="profile">
        <TabsList>
          <TabsTrigger value="profile" data-testid="tab-profile">Profile</TabsTrigger>
          <TabsTrigger value="statuses" data-testid="tab-statuses">Asset Statuses</TabsTrigger>
          <TabsTrigger value="types" data-testid="tab-types">Asset Types</TabsTrigger>
          <TabsTrigger value="industry" data-testid="tab-industry">Industry</TabsTrigger>
          <TabsTrigger value="qr" data-testid="tab-qr">QR Settings</TabsTrigger>
          <TabsTrigger value="sync" data-testid="tab-sync">Inventory Sync</TabsTrigger>
        </TabsList>

        <TabsContent value="sync" className="mt-4"><InventorySyncSettings /></TabsContent>


        <TabsContent value="profile" className="mt-4">
          <div className="rounded-lg border border-zinc-200 bg-white p-6 space-y-4">
            <div className="flex items-center gap-4">
              {company.logo_url ? (
                <img src={mediaUrl(company.logo_url)} alt="Logo" className="h-14 w-14 rounded-lg object-cover border border-zinc-200" data-testid="settings-logo-preview" />
              ) : (
                <div className="h-14 w-14 rounded-lg bg-emerald-50 flex items-center justify-center"><Buildings size={28} weight="bold" className="text-emerald-600" /></div>
              )}
              <div><p className="font-display font-bold text-lg">{company.name}</p><p className="text-sm text-zinc-500">Plan: <span className="capitalize font-medium">{company.plan}</span></p></div>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div><Label className="text-sm font-semibold">Company name</Label><Input value={company.name || ""} onChange={set("name")} className="h-11 mt-1.5 focus:ring-2 focus:ring-emerald-500" data-testid="company-name" /></div>
              <div><Label className="text-sm font-semibold">Phone</Label><Input value={company.phone || ""} onChange={set("phone")} className="h-11 mt-1.5 focus:ring-2 focus:ring-emerald-500" data-testid="company-phone" /></div>
              <div><Label className="text-sm font-semibold">Email</Label><Input value={company.email || ""} onChange={set("email")} className="h-11 mt-1.5 focus:ring-2 focus:ring-emerald-500" data-testid="company-email" /></div>
              <div><Label className="text-sm font-semibold">Website</Label><Input value={company.website || ""} onChange={set("website")} placeholder="www.yourcompany.com" className="h-11 mt-1.5 focus:ring-2 focus:ring-emerald-500" data-testid="company-website" /><p className="text-xs text-zinc-400 mt-1.5">Shown in the footer of printed QR labels.</p></div>
              <div className="sm:col-span-2">
                <Label className="text-sm font-semibold">Company Logo</Label>
                <input ref={logoRef} type="file" accept="image/*" className="hidden" onChange={(e) => uploadLogo(e.target.files[0])} data-testid="company-logo-input" />
                <div className="flex items-center gap-3 mt-1.5">
                  {company.logo_url && <img src={mediaUrl(company.logo_url)} alt="Logo" className="h-11 w-11 rounded-md object-cover border border-zinc-200" />}
                  <Button variant="outline" onClick={() => logoRef.current.click()} disabled={uploadingLogo} className="h-11 gap-2 border-zinc-300" data-testid="company-logo-upload">
                    {uploadingLogo ? <Spinner size={18} className="animate-spin" /> : <UploadSimple size={18} />} {company.logo_url ? "Replace Logo" : "Upload Logo"}
                  </Button>
                </div>
                <p className="text-xs text-zinc-400 mt-1.5">Shown on the sidebar and printed QR labels. PNG or JPG.</p>
              </div>
              <div className="sm:col-span-2"><Label className="text-sm font-semibold">Address</Label><Input value={company.address || ""} onChange={set("address")} className="h-11 mt-1.5 focus:ring-2 focus:ring-emerald-500" data-testid="company-address" /></div>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="statuses" className="mt-4">
          <div className="rounded-lg border border-zinc-200 bg-white p-6">
            <p className="text-sm text-zinc-500 mb-4">Customize the asset statuses used across your inventory.</p>
            <div className="flex flex-wrap gap-2 mb-4">
              {statuses.map((s) => (
                <Badge key={s} variant="outline" className="rounded-md h-9 px-3 gap-2 text-sm" data-testid={`status-chip-${s}`}>
                  {s}
                  <button onClick={() => setStatuses(statuses.filter((x) => x !== s))} className="text-zinc-400 hover:text-red-500"><X size={14} weight="bold" /></button>
                </Badge>
              ))}
            </div>
            <div className="flex gap-2">
              <Input value={newStatus} onChange={(e) => setNewStatus(e.target.value)} placeholder="New status" className="h-11 max-w-xs focus:ring-2 focus:ring-emerald-500" data-testid="new-status-input" />
              <Button onClick={() => { if (newStatus && !statuses.includes(newStatus)) { setStatuses([...statuses, newStatus]); setNewStatus(""); } }} variant="outline" className="h-11 gap-1.5 border-zinc-300" data-testid="add-status"><Plus size={16} /> Add</Button>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="types" className="mt-4">
          <CustomTypesManager />
        </TabsContent>

        <TabsContent value="industry" className="mt-4">
          <IndustrySettings company={company} onChanged={(industry) => { setCompany({ ...company, industry }); refreshCompany(); }} />
        </TabsContent>

        <TabsContent value="qr" className="mt-4">
          <div className="rounded-lg border border-zinc-200 bg-white p-6 space-y-4">
            <div className="flex items-center justify-between">
              <div><Label className="text-sm font-semibold">Include company name on labels</Label><p className="text-xs text-zinc-500">Show your company name on printed QR labels.</p></div>
              <Switch checked={company.qr_settings?.include_company ?? true} onCheckedChange={(v) => setCompany({ ...company, qr_settings: { ...company.qr_settings, include_company: v } })} data-testid="qr-include-company" />
            </div>
          </div>
        </TabsContent>
      </Tabs>

      <Button onClick={save} disabled={saving} className="h-12 bg-emerald-500 hover:bg-emerald-600 text-white font-semibold" data-testid="save-settings">{saving ? <Spinner size={20} className="animate-spin" /> : "Save Changes"}</Button>
    </div>
  );
}
